import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SseClient } from 'ngx-sse-client';
import { firstValueFrom, Subject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';

const conversation = 'conversation';

const apiRoutes = {
	conversation: conversation,
	signedURL: conversation + '/get-upload-url',
}

@Injectable({ providedIn: 'root' })
export class ConversationService {

	private refreshConversaionList = new Subject<void>();
	private uploadStatusSubject = new Subject<string>();
	private progressConversationDataSubject = new Subject<{ data: any, progress: number }>();
	withCredentials = { withCredentials: true };

	sseSubscription!: Subscription;

	progress_check = 0;

	constructor(private http: HttpClient, private sseClient: SseClient, private authService: AuthService) { }

	refreshList() {
    this.refreshConversaionList.next();
  }

	getRefreshObservable() {
    return this.refreshConversaionList.asObservable();
  }

	getUploadStatusSubject() {
    return this.uploadStatusSubject.asObservable();
  }

	getProgressConversationDataSubject() {
    return this.progressConversationDataSubject.asObservable();
  }

	getProjectConversations(project_id: number, params: any) {
		return this.http.get<any>(environment.api + apiRoutes.conversation + '/' + project_id, { params: params });
	}

	getConversations(params: any) {
		return this.http.get<any>(environment.api + apiRoutes.conversation + '/all', { params: params });
	}

	getProjectConversation(project_id: number, conversation_id: number) {
		return this.http.get<any>(environment.api + apiRoutes.conversation + '/' + project_id + '/' + conversation_id);
	}

	createConversation(project_id: number, data: any) {
		return this.http.post<any>(environment.api + apiRoutes.conversation + '/' + project_id, data);
	}

	deleteConversation(conversation_id: number) {
		return this.http.delete<any>(environment.api + apiRoutes.conversation + '/' + conversation_id);
	}

	updateConversation(project_id: number, conversation_id: number, data: any) {
		return this.http.patch<any>(environment.api + apiRoutes.conversation + '/' + project_id + '/' + conversation_id, data);
	}

	mergeChunks(project_id: number, conversation_id: number, data: any) {
		return this.http.post<any>(environment.api + apiRoutes.conversation + '/sent-to-merge/' + project_id + '/' + conversation_id, data);
	}

	async updateConversationStatus(project_id: number, conversation_id: number, data: any) {
		const response$ = this.http.patch<any>(environment.api + apiRoutes.conversation + '/' + project_id + '/' + conversation_id, data);
		const response = await firstValueFrom(response$);
    return response;
	}

	async getUploadURL(file_name: string, file_type: string) {
		const response$ = this.http.post<any>(environment.api + apiRoutes.conversation + '/get-upload-url', { file_name, file_type });
		const response = await firstValueFrom(response$);
    return response;
	}

	getProjectConversationData(project_id: number, conversation_id: number) {
		const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Authorization': `Bearer ${this.authService.token}`, 'client_id': this.authService.clientID });
		const url = environment.api + apiRoutes.conversation + '/data/' + project_id + '/' + conversation_id;
		this.sseSubscription = this.sseClient.stream(url, { keepAlive: false, reconnectionDelay: 0, responseType: 'event' }, { headers, withCredentials: true }, 'GET').subscribe(
			(event) => {
				const messageEvent = event as MessageEvent;

				if (event.type === 'error') {
					if (this.progress_check < 100) {
						// send the option to retry;
					}
					this.sseSubscription.unsubscribe();
				}

				if (!messageEvent.data) {
					this.sseSubscription.unsubscribe();
				} else {
					const data = JSON.parse(messageEvent.data);
					if (data.progress) {
						this.progress_check = data.progress;
						this.progressConversationDataSubject.next({ data: data.message, progress: data.progress });
					}
				}
			}
		);
	}

	uploadConversationFile = async (file: File, onProgress: (loadedBytes: number, totalBytes: number) => void, abortSignal?: AbortSignal) => {
		const fileName = `conversations-videos/${file.name}`;
		const signedUrl = await this.getSignedUrl(fileName, file.type);

		const xhr = new XMLHttpRequest();
		xhr.open('PUT', signedUrl, true);
		xhr.setRequestHeader('Content-Type', file.type);

		xhr.upload.onprogress = (event) => {
			if (event.lengthComputable) {
				if (abortSignal && abortSignal.aborted) {
					xhr.abort();
					return;
				}

				onProgress(event.loaded, event.total);
			}
		};

		xhr.onload = () => {
			this.uploadStatusSubject.next('complete');
		};

		xhr.onerror = async () => {
			this.uploadStatusSubject.next('error');
		};

		xhr.onabort = () => {
			this.uploadStatusSubject.next('abort');
		};

		xhr.send(file);
	}

	async getSignedUrl(file_name: string, file_type: string): Promise<any> {
		const response$ = this.http.post<any>(environment.api + apiRoutes.signedURL, { file_name, file_type });
		const response = await firstValueFrom(response$);
		return response;
	}
}